/**
 * 实时更新（SSE）运行配置 —— 单一事实来源。
 *
 * 默认连接后端 `/api/stream/changes`。浏览器可通过 `realtime:sse-enabled` 覆盖默认开关。
 *
 * 说明：开关只决定「是否连这条流」，不影响任何页面既有的请求与刷新逻辑。
 */
export interface RealtimeConfig {
  /** 构建期默认开关；运行期可被 localStorage 覆盖 */
  enabled: boolean;
  /** SSE 端点（同源，走 /api 代理） */
  url: string;
  /** 多久没收到任何字节（含注释心跳帧）就认为连接已死并重连 */
  heartbeatTimeoutMs: number;
  /** 首次重连延迟 */
  reconnectBaseDelayMs: number;
  /** 指数退避上限 */
  reconnectMaxDelayMs: number;
  /** 抖动比例（0~1），用于打散多客户端的重连时刻 */
  reconnectJitterRatio: number;
  /** 脏标记合并窗口：窗口内的多次事件只通知一次 */
  mergeWindowMs: number;
  /** 同一页面两次刷新之间的最小间隔 */
  refreshThrottleMs: number;
}

export const realtimeConfig: RealtimeConfig = {
  enabled: true,
  url: '/api/stream/changes',
  heartbeatTimeoutMs: 60_000,
  reconnectBaseDelayMs: 1_000,
  reconnectMaxDelayMs: 30_000,
  reconnectJitterRatio: 0.2,
  mergeWindowMs: 500,
  refreshThrottleMs: 1_000,
};

/** 运行期开关的存储键 */
export const REALTIME_ENABLED_STORAGE_KEY = 'realtime:sse-enabled';

/** 运行期开关变化事件（同标签页内生效；跨标签页由 storage 事件覆盖） */
export const REALTIME_ENABLED_CHANGE_EVENT = 'realtime:enabled-change';

/** 当前是否开启实时更新。localStorage 未设置时回落到构建期默认值。 */
export function isRealtimeEnabled(): boolean {
  if (typeof window === 'undefined') return realtimeConfig.enabled;
  try {
    const raw = window.localStorage.getItem(REALTIME_ENABLED_STORAGE_KEY);
    if (raw === null) return realtimeConfig.enabled;
    return raw === '1' || raw === 'true';
  } catch {
    return realtimeConfig.enabled;
  }
}

/**
 * 写入运行期开关并广播变化。仅供需要提供「实时开关」入口的代码/调试使用；
 * 普通用户直接在控制台写 localStorage 后刷新即可。
 */
export function setRealtimeEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      REALTIME_ENABLED_STORAGE_KEY,
      enabled ? '1' : '0',
    );
  } catch {
    // 隐私模式等场景写不进去：本次广播仍然生效
  }
  window.dispatchEvent(
    new CustomEvent(REALTIME_ENABLED_CHANGE_EVENT, { detail: enabled }),
  );
}

export function getRealtimeStreamUrl(): string {
  return realtimeConfig.url;
}
